'use client';

import React, { useState, useEffect } from 'react';
import { Check, Plus } from 'lucide-react';
import { getActiveServices, formatPrice, formatDuration, Service } from '@/lib/bookingStore';

const services: Service[] = getActiveServices().filter(s =>
  ['Axila', 'Virilha Simples', 'Virilha Completa', 'Buço', 'Queixo', 'Barriga', 'Braço Inteiro', 'Meio Braço', 'Perna Inteira', 'Meia Perna'].includes(s.name)
);

const serviceImages: Record<string, string> = {
  'Axila': '/assets/images/Promocao_mes_das_mulheres_depilacao_a_laser_delicado_post_do_instagram-1789416027735.png',
  'Virilha Simples': '/assets/images/maos_magicas_services.png',
  'Virilha Completa': '/assets/images/maos_magicas_services.png',
  'Buço': '/assets/images/maos_magicas_services.png',
  'Queixo': '/assets/images/maos_magicas_services.png',
  'Barriga': '/assets/images/maos_magicas_services.png',
  'Braço Inteiro': '/assets/images/maos_magicas_services.png',
  'Meio Braço': '/assets/images/maos_magicas_services.png',
  'Perna Inteira': '/assets/images/maos_magicas_services.png',
  'Meia Perna': '/assets/images/maos_magicas_services.png',
};

const serviceDescriptions: Record<string, string> = {
  'Axila': 'Depilação completa da axila com cera quente de alta qualidade.',
  'Virilha Simples': 'Depilação da virilha simples com técnica precisa e confortável.',
  'Virilha Completa': 'Depilação completa da virilha para um resultado impecável.',
  'Buço': 'Remoção suave e eficaz dos pelos do buço com cera fria.',
  'Queixo': 'Depilação delicada da região do queixo com cera especial.',
  'Barriga': 'Depilação da região abdominal com cera quente premium.',
  'Braço Inteiro': 'Depilação completa do braço do ombro ao pulso.',
  'Meio Braço': 'Depilação da metade inferior do braço com cera.',
  'Perna Inteira': 'Depilação completa da perna do tornozelo à coxa.',
  'Meia Perna': 'Depilação da metade inferior da perna com cera quente.',
};

export default function ServicesSection({ onSelectionChange }: { onSelectionChange?: (ids: string[]) => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  React.useEffect(() => {
    onSelectionChange?.(Array.from(selected));
  }, [selected, onSelectionChange]);

  const selectedServices = services.filter(s => selected.has(s.id));
  const totalPrice = selectedServices.reduce((sum, s) => sum + s.price, 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration, 0);

  const bookingUrl = selected.size > 0
    ? `#agendar`
    : '#agendar';

  const handleAgendar = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById('agendar');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="servicos" className="py-24 lg:py-32" style={{ background: '#FFC2D4' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-xl mb-16">
          <span className="gold-line mb-6 block" />
          <p className="text-xs tracking-[0.25em] uppercase font-medium text-burnt-rose mb-4">
            Os Nossos Serviços
          </p>
          <h2 className="font-serif text-4xl lg:text-5xl font-light leading-[1.1] text-dark-brown mb-5">
            Depilação a cera<br />
            <em className="italic text-burnt-rose">com precisão e cuidado.</em>
          </h2>
          <p className="text-foreground/60 text-base leading-relaxed">
            Selecione os serviços que deseja e agende a sua sessão. Pode combinar vários tratamentos numa única visita.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
          {services.map(service => {
            const isSelected = selected.has(service.id);
            return (
              <div
                key={service.id}
                className={`service-card cursor-pointer overflow-hidden ${isSelected ? 'service-card-selected' : ''}`}
                onClick={() => toggle(service.id)}
              >
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={serviceImages[service.name] || '/assets/images/maos_magicas_services.png'}
                    alt={`${service.name} — depilação a cera na Mãos Mágicas`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-brown/30 to-transparent" />
                  {/* Selection indicator */}
                  <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                    isSelected ? 'bg-burnt-rose' : 'bg-white/80 border border-border'
                  }`}>
                    {isSelected
                      ? <Check size={14} className="text-white" strokeWidth={2.5} />
                      : <Plus size={14} className="text-foreground/50" strokeWidth={2} />
                    }
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-serif text-lg font-medium text-dark-brown mb-1">{service.name}</h3>
                  <p className="text-xs text-foreground/60 leading-relaxed mb-3 line-clamp-2">
                    {serviceDescriptions[service.name] || service.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-burnt-rose text-base">{formatPrice(service.price)}</span>
                    <span className="text-xs text-foreground/50 bg-muted px-2 py-1 rounded-full">
                      {formatDuration(service.duration)}
                    </span>
                  </div>
                  <button
                    className={`mt-3 w-full py-2 rounded-lg text-xs font-semibold tracking-wide uppercase transition-all duration-200 ${
                      isSelected
                        ? 'bg-burnt-rose text-white' :'bg-blush text-burnt-rose hover:bg-dusty-rose'
                    }`}
                    onClick={e => { e.stopPropagation(); toggle(service.id); }}
                  >
                    {isSelected ? '✓ Adicionado' : 'Adicionar à marcação'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Floating summary */}
        {selected.size > 0 && (
          <div className="mt-10 bg-white border border-border rounded-2xl p-6 shadow-lg animate-slide-up">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs tracking-wide uppercase text-foreground/50 mb-1">A sua seleção</p>
                <p className="font-serif text-xl text-dark-brown">
                  {selected.size} serviço{selected.size > 1 ? 's' : ''} · <span className="text-burnt-rose">{formatPrice(totalPrice)}</span>
                </p>
                <p className="text-xs text-foreground/60 mt-0.5">Duração estimada: {formatDuration(totalDuration)}</p>
              </div>
              <a
                href={bookingUrl}
                onClick={handleAgendar}
                className="inline-flex items-center justify-center px-8 py-3.5 btn-primary rounded-full text-xs tracking-[0.12em] uppercase font-semibold whitespace-nowrap"
              >
                Agendar Sessão
              </a>
            </div>
          </div>
        )}

        {selected.size === 0 && (
          <div className="mt-10 text-center">
            <a
              href="#agendar"
              onClick={handleAgendar}
              className="inline-flex items-center justify-center px-10 py-4 btn-primary rounded-full text-sm tracking-[0.12em] uppercase font-semibold"
            >
              Ver Disponibilidade
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
